import { CSSProperties } from '@material-ui/core/styles/withStyles';
import React, {
  MouseEventHandler,
  MutableRefObject,
  ReactNode,
  useCallback,
  useMemo,
  useRef,
  useState,
} from 'react';
import './style.scss';
import { using } from 'services/TypeUtils';

type Register = {
  onMouseOver: MouseEventHandler;
  onMouseOut: MouseEventHandler;
  ref: React.RefObject<HTMLDivElement>;
};

type Props = {
  children: (register: Register) => ReactNode;
  tooltip?: ReactNode;
  style?: CSSProperties;
  placement?: 'right' | 'bottom';
};

/**
 * A replacement for react-tooltip.
 * Shows the tooltip to the right of the element by default, or below it
 * (right-aligned to the trigger) when placement="bottom".
 */
const Tooltip2 = ({ children, tooltip, style, placement = 'right' }: Props) => {
  const [showTooltip, setShowTooltip] = useState(false);
  const [tooltipOffset, setTooltipOffset] = useState(['0px', '0px']);
  const ref = useRef<HTMLDivElement>(null);
  const shouldShow = useRef<Boolean>(false);

  const onMouseOver = useCallback(() => {
    shouldShow.current = true;
    setTimeout(() => {
      if (!shouldShow.current) {
        return;
      }
      setShowTooltip(true);
      const rect = using(ref.current?.getBoundingClientRect(), (rect) => {
        return placement === 'bottom'
          ? [rect.right + 'px', rect.bottom + 'px']
          : [rect.left + rect.width + 'px', rect.top + 'px'];
      });

      setTooltipOffset(rect ?? ['0px', '0px']);
    }, 100);
  }, [placement]);

  const onMouseOut = useCallback(() => {
    shouldShow.current = false;
    setShowTooltip(false);
  }, []);

  const register: Register = useMemo(
    () => ({
      ref,
      onMouseOver,
      onMouseOut,
    }),
    [],
  );

  if (!tooltip) return children(register);

  return (
    <div
      style={{
        //@ts-expect-error TODO find how other people handle the types here
        '--tooltip-offset-left': tooltipOffset[0],
        '--tooltip-offset-top': tooltipOffset[1],
      }}
    >
      {children(register)}
      {showTooltip && (
        <div
          className={`tooltip2-wrapper careand-tooltip wide tooltip2-${placement}`}
          style={style}
        >
          {tooltip}
        </div>
      )}
    </div>
  );
};

export default Tooltip2;
